import Head from "next/head";

const SITE_NAME = "Weingut Johann Gisperg";
const SITE_URL = "https://www.gisperg.wine";
const DEFAULT_DESCRIPTION =
    "Weingut Johann Gisperg in der Thermenregion — 1000 Momente in einem Glas. Online-Shop, Heuriger, Termine und mehr.";
const DEFAULT_IMAGE = "/images/poster.webp";

/**
 * Centralised head metadata for every page. Sets the title, description,
 * canonical URL, viewport, OpenGraph + Twitter Card so social shares look
 * right and Lighthouse SEO stops complaining about missing meta.
 *
 * Usage:
 *   <Seo
 *     title="Bestellen | Weingut Gisperg"
 *     description="…"
 *     path="/weinkauf"
 *   />
 *
 * `path` is the canonical pathname (no domain). Falls back to `/` if omitted.
 */
export default function Seo({
    title,
    description,
    path = "/",
    image,
    noindex = false,
    type = "website",
}) {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const desc = description || DEFAULT_DESCRIPTION;
    const canonical = `${SITE_URL}${path.endsWith("/") ? path : `${path}/`}`;
    const ogImage = image
        ? image.startsWith("http")
            ? image
            : `${SITE_URL}${image}`
        : `${SITE_URL}${DEFAULT_IMAGE}`;

    return (
        <Head>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            <meta
                name="viewport"
                content="width=device-width, initial-scale=1, viewport-fit=cover"
            />
            <link rel="canonical" href={canonical} />
            {noindex ? (
                <meta name="robots" content="noindex,nofollow" />
            ) : (
                <meta name="robots" content="index,follow" />
            )}

            {/* OpenGraph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:locale" content="de_AT" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImage} />
        </Head>
    );
}
